import React from "react";
import { motion } from "framer-motion";
import { Phone, FileText, Sparkles, ShieldCheck } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

const options = [
  {
    title: "Chat on WhatsApp",
    desc: "Best for quick replies, direct conversation, and easy requirement sharing.",
    buttonText: "Open WhatsApp",
    href: "https://wa.me/919665855163?text=Hi%20A2Z%20Consultancy,%20I%20would%20like%20to%20inquire%20about%20hiring%20maid,%20cook,%20driver,%20or%20caretaker%20services.",
    icon: FaWhatsapp,
    type: "whatsapp",
    badge: "Fastest",
  },
  {
    title: "Call Us Directly",
    desc: "Ideal for urgent needs or if you want to discuss your requirement immediately.",
    buttonText: "Call Now",
    href: "tel:+919665855163",
    icon: Phone,
    type: "call",
    badge: "Direct",
  },
  {
    title: "Fill Quick Request Form",
    desc: "Perfect if you want to share complete details and let us follow up with you.",
    buttonText: "Open Form",
    href: "https://forms.gle/ZTXKbyfv1snHH2Hp6",
    icon: FileText,
    type: "form",
    badge: "Detailed",
  },
];

const getCardClasses = (type) => {
  if (type === "whatsapp") {
    return {
      card: "bg-gradient-to-br from-[#25D366] to-[#128C7E] text-white border-transparent",
      glow: "from-white/12 via-transparent to-white/8",
      chip: "bg-white/18 text-white border-white/20",
    };
  }

  if (type === "call") {
    return {
      card: "bg-gradient-to-br from-sky-500 to-blue-600 text-white border-transparent",
      glow: "from-white/12 via-transparent to-white/8",
      chip: "bg-white/18 text-white border-white/20",
    };
  }

  return {
    card: "bg-gradient-to-br from-indigo-500 to-purple-600 text-white border-transparent",
    glow: "from-white/12 via-transparent to-white/8",
    chip: "bg-white/18 text-white border-white/20",
  };
};

const HireContactOptions = () => {
  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#fdf8ff_0%,#f3efff_45%,#f7f4ff_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_14%_18%,rgba(99,102,241,0.10),transparent_22%),radial-gradient(circle_at_84%_20%,rgba(168,85,247,0.08),transparent_20%),radial-gradient(circle_at_80%_82%,rgba(59,130,246,0.06),transparent_22%)]" />
      <div className="absolute top-[-100px] right-[-80px] w-[240px] h-[240px] bg-purple-300/20 rounded-full blur-3xl" />
      <div className="absolute bottom-[-100px] left-[-80px] w-[240px] h-[240px] bg-indigo-300/20 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="text-center mb-14 md:mb-16"
        >
          <span className="inline-flex items-center rounded-full border border-indigo-200 bg-white/80 backdrop-blur px-4 py-2 text-sm text-indigo-700 font-medium mb-5 shadow-sm">
            Fast & Easy Contact
          </span>

          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Choose How You Want to Reach Us
          </h2>

          <p className="text-gray-600 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            No long forms and no complicated steps. Pick the method that feels easiest
            for you and connect instantly.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-5 md:gap-6">
          {options.map((option, i) => {
            const Icon = option.icon;
            const isExternal = option.href.startsWith("http");
            const styles = getCardClasses(option.type);

            return (
              <motion.div
                key={option.title}
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: i * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className={`group relative rounded-[28px] p-6 md:p-7 shadow-[0_12px_35px_rgba(0,0,0,0.10)] hover:shadow-[0_18px_45px_rgba(0,0,0,0.16)] transition-all duration-300 overflow-hidden flex flex-col h-full ${styles.card}`}
              >
                {/* Hover glow */}
                <div
                  className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-300 bg-gradient-to-br ${styles.glow} pointer-events-none`}
                />

                {/* Dotted art */}
                <div className="absolute top-3 right-3 opacity-[0.12]">
                  <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                    {Array.from({ length: 5 }).map((_, row) =>
                      Array.from({ length: 5 }).map((__, col) => (
                        <circle
                          key={`${row}-${col}`}
                          cx={7 + col * 11}
                          cy={7 + row * 11}
                          r="2"
                          fill="white"
                        />
                      ))
                    )}
                  </svg>
                </div>

                {/* Top row */}
                <div className="relative flex items-start justify-between gap-3 mb-5">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center bg-white/20 border border-white/15 shrink-0">
                    <Icon className="w-7 h-7 text-white" />
                  </div>

                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${styles.chip}`}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {option.badge}
                  </span>
                </div>

                {/* Title */}
                <h3 className="relative text-xl md:text-2xl font-semibold mb-3 text-white">
                  {option.title}
                </h3>

                {/* Description */}
                <p className="relative text-sm md:text-[15px] leading-6 mb-6 text-white/90">
                  {option.desc}
                </p>

                {/* Mid strip */}
                <div className="relative rounded-2xl border border-white/15 bg-white/10 px-4 py-3 mb-6">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-white/85">Best for</span>
                    <span className="text-sm font-semibold text-white">
                      {option.badge}
                    </span>
                  </div>
                </div>

                {/* Button aligned bottom */}
                <div className="mt-auto">
                  <a
                    href={option.href}
                    target={isExternal ? "_blank" : "_self"}
                    rel={isExternal ? "noreferrer" : undefined}
                    className="relative block"
                  >
                    <button className="w-full py-3.5 md:py-4 rounded-2xl font-semibold transition-all duration-300 bg-white text-gray-900 hover:shadow-lg hover:scale-[1.02]">
                      {option.buttonText}
                    </button>
                  </a>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom trust strip */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="mt-12 md:mt-14"
        >
          <div className="relative overflow-hidden rounded-[28px] border border-white/70 bg-white/78 backdrop-blur-xl shadow-[0_12px_35px_rgba(15,23,42,0.06)] px-5 py-5 md:px-6">
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5 text-indigo-600" />
                </div>

                <div>
                  <p className="text-sm font-semibold text-indigo-700 mb-1">
                    Simple and trusted communication
                  </p>
                  <p className="text-sm md:text-base text-gray-600">
                    Reach us in the way that feels most comfortable for you and we will help you move forward smoothly.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-2 rounded-full border border-green-100 bg-green-50 text-green-700 text-sm font-medium">
                  WhatsApp Friendly
                </span>
                <span className="px-3 py-2 rounded-full border border-sky-100 bg-sky-50 text-sky-700 text-sm font-medium">
                  Easy Contact
                </span>
                <span className="px-3 py-2 rounded-full border border-indigo-100 bg-indigo-50 text-indigo-700 text-sm font-medium">
                  Fast Response
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HireContactOptions;